/**
 * LanguageSwitcher — the one control every surface uses to change the UI
 * language (landing header for anonymous visitors, app header, Profile page).
 *
 * A native <select> (keyboard + screen-reader friendly, no popover code). On
 * change it calls `switchLocale`, which persists the choice to localStorage,
 * fetches the target catalog (non-default locales are lazy chunks — the control
 * is disabled + aria-busy meanwhile), saves it to the signed-in user's profile
 * and reloads into the new language.
 */

import { useState } from 'react';

import { LOCALE_META, SUPPORTED_LOCALES, isLocale, type Locale } from './config';
import { switchLocale } from './profileLocale';
import { useLocaleStore } from './store';
import { useT } from './useT';

export interface LanguageSwitcherProps {
  /** `compact` = header-sized; `field` = full-width form control. */
  variant?: 'compact' | 'field';
  id?: string;
  className?: string;
}

export function LanguageSwitcher({ variant = 'compact', id, className }: LanguageSwitcherProps) {
  const { t, locale } = useT();
  const [busy, setBusy] = useState(false);
  // Non-default locales are lazy chunks: while the target catalog downloads the
  // control stays disabled + aria-busy (then the page reloads into the language).
  const loadingLocale = useLocaleStore((s) => s.loadingLocale);
  const pending = busy || loadingLocale !== null;

  const onChange = async (value: string) => {
    if (!isLocale(value) || value === locale) return;
    setBusy(true);
    try {
      await switchLocale(value as Locale);
    } finally {
      setBusy(false);
    }
  };

  const base =
    variant === 'field'
      ? 'w-full rounded-token border border-border bg-surface px-3 py-2 text-sm text-foreground'
      : 'rounded-token border border-border bg-surface px-2 py-1.5 text-xs font-medium text-foreground';

  return (
    <select
      id={id}
      data-testid="language-switcher"
      aria-label={t('common.language')}
      title={t('common.language')}
      value={loadingLocale ?? locale}
      disabled={pending}
      aria-busy={pending || undefined}
      data-loading={pending ? 'true' : undefined}
      onChange={(e) => void onChange(e.target.value)}
      className={`${base} focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60 ${className ?? ''}`}
    >
      {SUPPORTED_LOCALES.map((l) => (
        <option key={l} value={l} lang={l}>
          {LOCALE_META[l].nativeName}
        </option>
      ))}
    </select>
  );
}
